/**
 * Les trois boutons d'après-exercice et ce que la page en dit à l'élève : leurs libellés, le repère
 * de progression, l'annonce « connaissance acquise » et la cible du bouton « Connaissance suivante ».
 * Du texte et des décisions pures, sans React : la page et ses tests s'appuient dessus.
 */
import {
  ALL_NODES,
  GODOT_ALL_NODES,
  PYJS_ALL_NODES,
  type KnowledgePointProgressView,
  type LearningNode,
} from '@unity-learning/shared';

/** Les deux premiers boutons s'appellent pareil partout (parcours, sur mesure, exploration libre, quête). */
export const FOLLOW_UP_LABELS = {
  /** `followUp.kind = 'continue'` */
  continue: 'Même niveau, autre situation',
  /** `followUp.kind = 'next'` */
  next: 'Augmenter la difficulté',
} as const;

export type FollowUpAction = 'continue' | 'next' | 'knowledge';

/** Ce que « Connaissance suivante » fait générer : un point de la même étape, ou le début de l'étape d'après. */
export type KnowledgeTarget =
  | { kind: 'point'; nodeId: string; kpId: string; label: string }
  | { kind: 'step'; nodeId: string; kpId: null; label: string };

/**
 * La cible du troisième bouton, ou `null` quand il n'y en a pas : progression inconnue, dernier point du
 * parcours, ou étape suivante pas encore ouverte (l'élève doit d'abord valider l'étape en cours).
 */
export function knowledgeTarget(progress: KnowledgePointProgressView | null): KnowledgeTarget | null {
  if (!progress) return null;
  if (progress.nextPoint) {
    return {
      kind: 'point',
      nodeId: progress.step.id,
      kpId: progress.nextPoint.id,
      label: `Connaissance suivante : ${progress.nextPoint.name}`,
    };
  }
  if (progress.nextStep?.open) {
    return {
      kind: 'step',
      nodeId: progress.nextStep.id,
      kpId: null,
      label: `Étape suivante : ${progress.nextStep.title}`,
    };
  }
  return null;
}

/** Phrase sous les boutons quand le troisième n'existe pas parce que l'étape suivante est fermée, sinon `null`. */
export function stepClosedNote(progress: KnowledgePointProgressView | null): string | null {
  if (!progress || progress.nextPoint || !progress.nextStep || progress.nextStep.open) return null;
  return `C'est la dernière connaissance de cette étape. L'étape ${progress.nextStep.number} « ${progress.nextStep.title} » s'ouvrira quand vous aurez validé celle-ci.`;
}

const reussis = (n: number) => `${n} ${n > 1 ? 'réussis' : 'réussi'}`;
const exercicesFaits = (n: number) => `${n} ${n > 1 ? 'exercices faits' : 'exercice fait'}`;

/** Réussites montrées à l'élève : jamais au-dessus du seuil (les anciens compteurs pouvaient le dépasser de beaucoup). */
function shownSuccesses(progress: KnowledgePointProgressView): number {
  return Math.min(progress.successes, progress.threshold);
}

/**
 * Le repère en haut de l'exercice :
 * « Étape 1 · Connaissance 3 sur 34 : Assignation int · 2 réussis sur 3 · 4 exercices faits ».
 */
export function progressLine(progress: KnowledgePointProgressView): string {
  const { step, position, point, threshold } = progress;
  return [
    `Étape ${step.number}`,
    `Connaissance ${position.index} sur ${position.total} : ${point.name}`,
    `${reussis(shownSuccesses(progress))} sur ${threshold}`,
    exercicesFaits(progress.attempts),
  ].join(' · ');
}

/** Ce qui vient après une connaissance acquise, en une phrase. */
function nextSentence(progress: KnowledgePointProgressView): string {
  if (progress.nextPoint) return `Prochaine : ${progress.nextPoint.name}.`;
  if (progress.nextStep?.open) return `Prochaine : étape ${progress.nextStep.number} « ${progress.nextStep.title} ».`;
  if (progress.nextStep) {
    return `C'est la dernière de cette étape. L'étape ${progress.nextStep.number} « ${progress.nextStep.title} » s'ouvrira quand vous aurez validé celle-ci.`;
  }
  return 'C\'est la dernière connaissance du parcours.';
}

/** L'annonce dans la correction, à la réussite qui atteint le seuil. */
export function acquiredNotice(progress: KnowledgePointProgressView): string {
  return `✅ Connaissance acquise : ${progress.point.name} (${reussis(shownSuccesses(progress))} sur ${progress.threshold}). ${nextSentence(progress)}`;
}

/** Un nœud du parcours par son id, dans les trois curriculums (les id ne se recoupent pas). */
export function findPathNode(nodeId: string): LearningNode | undefined {
  return [...ALL_NODES, ...PYJS_ALL_NODES, ...GODOT_ALL_NODES].find(node => node.id === nodeId);
}

/**
 * La demande que le parcours envoie à la génération pour un nœud (et un point). C'est une consigne de la
 * plateforme, jamais une demande écrite par l'élève : le serveur la reconnaît à son début.
 */
export function buildPathCustomPrompt(nodeId: string, knowledgePointIds?: string[]): string {
  return knowledgePointIds && knowledgePointIds.length > 0
    ? `Exercice pour les points de connaissance spécifiques: ${knowledgePointIds.join(', ')}. Nœud: ${nodeId}. Respectez le niveau de difficulté configuré.`
    : `Exercice pour le nœud d'apprentissage: ${nodeId}. Respectez le niveau de difficulté configuré par l'utilisateur.`;
}
